import { Link, useSearchParams } from 'react-router-dom';
import { useFetchData } from '../../../hooks';
import { PageableResponse } from '../../../types';
import { RequestStatusEnum, Resource } from '../../../lib/enums';
import {
  convertDate,
  dateFormat,
  dateTimeFormat,
  getEnumValue,
} from '../../../lib/utils';
import React, { useEffect, useState } from 'react';
import { ReceivingReportRecord } from '../../../types/ReceivingReport';
import { RRS_API } from '../../../hooks/useReceivingReportHooks';
import { Table, TableColumnsType } from 'antd';
import StatusTag from '../../../components/ui/StatusTag';
import ErrorModal from '../../../components/ui/modal/ErrorModal';
import dayjs from 'dayjs';

const ReceivingReportTable = () => {
  const [searchParams] = useSearchParams();

  const {
    data: data,
    isLoading,
    error,
    isError,
  } = useFetchData<PageableResponse<ReceivingReportRecord>>(RRS_API, {
    resource: searchParams.get('resource') || Resource.FULL,
    search: searchParams.get('search') || '',
    from:
      searchParams.get('from') === undefined ||
      searchParams.get('from') === null
        ? dayjs().subtract(6, 'month').format(dateFormat)
        : searchParams.get('from'),
    to:
      searchParams.get('to') === undefined || searchParams.get('to') === null
        ? dayjs().add(2, 'month').format(dateFormat)
        : searchParams.get('to'),
    status: getEnumValue(RequestStatusEnum, searchParams.get('status') ?? ''),
    length: parseInt(searchParams.get('length') || '999999'),
  });
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  useEffect(() => {
    setIsErrorModalOpen(isError);
  }, [error?.message, isError]);

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };
  const columns: TableColumnsType<ReceivingReportRecord> = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 120,
      render: (_text, record) => (
        <Link to={`/stock-movement/receiving-report/${record.id}?action=view`}>
          <span className={'text-blue-400 font-semibold'}>
            {record.code || record.id}
          </span>
        </Link>
      ),
    },
    { title: 'Required Date', dataIndex: 'requiredDate' },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      render: (_text, record) => <span>{record.vendor?.description}</span>,
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      render: (_text, record) => <span>{record.destination?.description}</span>,
    },
    { title: 'Source', dataIndex: 'source' },
    { title: 'DR no', dataIndex: 'drNo' },
    { title: 'Invoice No', dataIndex: 'invoiceNo' },

    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => {
        return <StatusTag status={status} />;
      },
    },
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      render: (createdBy, record) => (
        <div className="flex flex-col">
          <span className="mr-2">{createdBy}</span>
          {record.timeCreated != null && (
            <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Updated By/Date',
      dataIndex: 'updatedBy',
      render: (updatedBy, record) => (
        <div className="flex flex-col">
          <span className="mr-2">{updatedBy}</span>
          {record.timeUpdated != null && (
            <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
          )}
        </div>
      ),
    },
  ];
  return (
    <>
      <div className={'flex flex-col h-[calc(79vh)] overflow-auto'}>
        <Table
          rowKey={(record) => record.id}
          dataSource={data?.data}
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          columns={columns}
        />
      </div>
      <ErrorModal
        visible={isErrorModalOpen}
        message={error?.message}
        onClose={handleErrorModalClose}
      />
    </>
  );
};

export default ReceivingReportTable;
